import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  TextField,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
  Sync,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../../../styles/swal-overrides.css';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import EscritorioSelect from '../../selects/EscritorioSelect';
import { ixcImportService, IxcClienteListItem, IxcImportProgressItem } from '../../../services/ixcImportService';

interface ImportIxcModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = ['Configuração da API IXC', 'Selecionar clientes', 'Importar'];

const STORAGE_KEY_BASE_URL = 'sysup_ixc_import_baseUrl';
const STORAGE_KEY_TOKEN = 'sysup_ixc_import_token';

export const ImportIxcModal: React.FC<ImportIxcModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ativos' | 'inativos' | 'todos'>('ativos');
  const [loading, setLoading] = useState(false);

  // Carregar URL e credencial do localStorage ao abrir o modal
  useEffect(() => {
    if (open) {
      try {
        const savedUrl = localStorage.getItem(STORAGE_KEY_BASE_URL);
        const savedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
        if (savedUrl) setBaseUrl(savedUrl);
        if (savedToken) setToken(savedToken);
      } catch {
        // ignora falha de localStorage
      }
    }
  }, [open]);

  // Salvar URL e credencial no localStorage quando alterar
  useEffect(() => {
    if (!open) return;
    try {
      if (baseUrl.trim()) localStorage.setItem(STORAGE_KEY_BASE_URL, baseUrl.trim());
      if (token) localStorage.setItem(STORAGE_KEY_TOKEN, token);
    } catch {
      // ignora falha de localStorage
    }
  }, [open, baseUrl, token]);
  const [error, setError] = useState<string | null>(null);
  const [clientes, setClientes] = useState<IxcClienteListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<IxcImportProgressItem[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'asc' }]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const handleBuscarClientes = async () => {
    if (!baseUrl.trim() || !token.trim()) {
      setError('Informe a URL base e o token da API IXC');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await ixcImportService.listClientes({
        baseUrl: baseUrl.trim(),
        token: token.trim(),
        status: statusFilter,
      });
      setClientes(list);
      setSelectedIds(new Set());
      setActiveStep(1);
      toast.success(`${list.length} clientes encontrados. Selecione os que deseja importar.`);
    } catch (err: any) {
      const msg = err?.message || 'Erro ao buscar clientes na API IXC';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = async () => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    const pageIdsNow = sortedRows.slice(start, end).map(r => r.rowId);

    const areAllVisibleSelected = pageIdsNow.length > 0 && pageIdsNow.every(id => selectedIds.has(id));
    const isAllInListSelected = clientes.length > 0 && clientes.every(c => selectedIds.has(String(c.id)));

    // Se tudo da página já está selecionado, o próximo clique deve desmarcar
    if (areAllVisibleSelected) {
      const next = new Set(selectedIds);
      // Se TUDO do IXC está selecionado, limpa tudo. Caso contrário, limpa só a página.
      if (isAllInListSelected) {
        clientes.forEach(c => next.delete(String(c.id)));
      } else {
        pageIdsNow.forEach(id => next.delete(id));
      }
      setSelectedIds(next);
      return;
    }

    // Se tem mais de uma página, pergunta o que selecionar
    const isMultiplePages = sortedRows.length > paginationModel.pageSize;

    if (isMultiplePages) {
      const pageItemsSnapshot = [...pageIdsNow];
      const pageItemsCount = pageItemsSnapshot.length;

      const result = await Swal.fire({
        title: 'Selecionar Clientes',
        text: `Deseja selecionar apenas os ${pageItemsCount} clientes desta página ou todos os ${clientes.length} encontrados?`,
        icon: 'question',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Todos os Registros',
        denyButtonText: 'Apenas esta Página',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#aaa',
        customClass: { container: 'swal-zindex' },
      });

      if (result.isConfirmed) {
        const next = new Set(selectedIds);
        clientes.forEach(c => next.add(String(c.id)));
        setSelectedIds(next);
      } else if (result.isDenied) {
        const next = new Set(selectedIds);
        pageItemsSnapshot.forEach(id => next.add(id));
        setSelectedIds(next);
      }
    } else {
      // Se só tem uma página, seleciona tudo direto
      const next = new Set(selectedIds);
      clientes.forEach(c => next.add(String(c.id)));
      setSelectedIds(next);
    }
  };

  const rows = useMemo(() => {
    return clientes.map((c, index) => ({
      ...c,
      rowId: String(c.id),
      _index: index,
    }));
  }, [clientes]);

  const sortedRows = useMemo(() => {
    if (!sortModel.length) return rows;
    const [s] = sortModel;
    const field = s.field as keyof IxcClienteListItem;
    const order = s.sort === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = a[field];
      const vb = b[field];
      const aNum = Number(va);
      const bNum = Number(vb);
      if (!isNaN(aNum) && !isNaN(bNum)) return (aNum - bNum) * order;
      return String(va).localeCompare(String(vb)) * order;
    });
  }, [rows, sortModel]);

  const currentPageItems = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return sortedRows.slice(start, end).map(r => r.rowId);
  }, [sortedRows, paginationModel]);


  const handleIniciarImportacao = () => {
    if (!escritorioId) {
      setError('Selecione o escritório');
      toast.error('Selecione o escritório');
      return;
    }
    if (selectedIds.size === 0) {
      setError('Selecione ao menos um cliente');
      toast.error('Selecione ao menos um cliente');
      return;
    }
    setActiveStep(2);
    setError(null);
    setImportResults([]);
  };

  const handleExecutarImportacao = async () => {
    if (!escritorioId || selectedIds.size === 0) return;
    setImporting(true);
    setError(null);
    setImportResults([]);

    const idsToImport = Array.from(selectedIds);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const idClienteIxc of idsToImport) {
        try {
          const result = await ixcImportService.importarUnico({
            baseUrl: baseUrl.trim(),
            token: token.trim(),
            idClienteIxc,
            escritorio_id: escritorioId,
          });

          setImportResults((prev) => [...prev, result]);
          if (result.status === 'sucesso') {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err: any) {
          errorCount++;
          // Adicionar item de erro se falhar a chamada HTTP básica
          setImportResults((prev) => [...prev, {
            id_ixc: idClienteIxc,
            nome: 'Erro na requisição',
            etapa: 'erro',
            status: 'erro',
            erro: err?.message || 'Falha ao conectar com o servidor'
          }]);
        }
      }

      if (successCount) toast.success(`${successCount} cliente(s) importado(s) com sucesso.`);
      if (errorCount) toast.warning(`${errorCount} cliente(s) com erro.`);
      if (errorCount === 0 && successCount > 0) onSuccess?.();
    } catch (err: any) {
      setError(err?.message || 'Erro na importação');
      toast.error(err?.message || 'Erro na importação');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setClientes([]);
    setSelectedIds(new Set());
    setEscritorioId(null);
    setError(null);
    setLoading(false);
    setImporting(false);
    setImportResults([]);
    onClose();
  };

  const renderStep0 = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API IXC (webservice v1) para listar e importar clientes.
      </Typography>
      <Card sx={{ mt: 1, p: 1.5 }}>
        <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
          <TextField
            label="URL base da API IXC"
            placeholder="https://seu-ixc.com.br"
            value={baseUrl}
            onChange={(e) => { setBaseUrl(e.target.value); setError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
            helperText="Ex: https://instancia.ixcsoft.com.br"
          />
          <TextField
            label="Credencial (id:token)"
            type="password"
            value={token}
            onChange={(e) => { setToken(e.target.value); setError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
            helperText="Formato: id do usuário : token da API"
          />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel id="status-filter-label">Status dos clientes</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status dos clientes"
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <MenuItem value="ativos">Somente ativos</MenuItem>
              <MenuItem value="inativos">Somente inativos</MenuItem>
              <MenuItem value="todos">Ativos e inativos</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={handleBuscarClientes}
            disabled={loading}
            startIcon={loading ? <Sync className="animate-spin" /> : <CloudUpload />}
          >
            {loading ? 'Buscando...' : 'Buscar clientes'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Selecione os clientes que deseja importar e o escritório de destino.
      </Typography>
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={sortedRows}
          getRowId={(row) => row.rowId}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              disableColumnMenu: true,
              width: 40,
              sortable: false,
              filterable: false,
              renderHeader: () => {
                const areAllVisibleSelected = currentPageItems.length > 0 && currentPageItems.every(id => selectedIds.has(id));
                const areAnyVisibleSelected = currentPageItems.some(id => selectedIds.has(id));

                return (
                  <IconButton size="small" onClick={toggleSelectAll} sx={{ p: 0.5 }}>
                    {areAllVisibleSelected ? (
                      <CheckBox fontSize="small" color="primary" sx={{ fontSize: '1rem' }} />
                    ) : areAnyVisibleSelected ? (
                      <IndeterminateCheckBox fontSize="small" color="primary" sx={{ fontSize: '1rem' }} />
                    ) : (
                      <CheckBoxOutlineBlank fontSize="small" sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                );
              },
              renderCell: (params) => {
                const rowId = params.row.rowId;
                const isSelected = selectedIds.has(rowId);
                return (
                  <IconButton
                    size="small"
                    onClick={() => toggleSelect(rowId)}
                    sx={{ p: 0.5 }}
                  >
                    {isSelected ? (
                      <CheckBox fontSize="small" color="primary" sx={{ fontSize: '1rem' }} />
                    ) : (
                      <CheckBoxOutlineBlank fontSize="small" sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                );
              },
            },
            {
              field: 'id',
              headerName: 'ID',
              width: 70,
              sortable: true,
              type: 'number',
            },
            {
              field: 'nome',
              headerName: 'Nome',
              flex: 1,
              minWidth: 150,
              sortable: true,
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 100,
              sortable: true,
              renderCell: (params) => (
                <Chip
                  label={String(params.value)}
                  size="small"
                  color={params.value === 'ativo' ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              ),
            },
          ]}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          density="compact"
          rowHeight={35}
          columnHeaderHeight={40}
          sx={{
            //formatar o header do data grid
            '& .MuiDataGrid-columnHeader': {
              bgcolor: (theme) => `color-mix(in srgb, #58b4ffff 12%, ${theme.palette.background.paper})`,
              fontWeight: 700,
              fontSize: '0.73rem',
              py: 0.8,
              borderBottom: `2px solid #58b4ffff`,
            },
            //formatar a paginação do data grid
            '.MuiTablePagination-toolbar': {
              minHeight: 20,
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
              '& .MuiTablePagination-spacer': { display: 'none' },
            },
            '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
            '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
            '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
            '.MuiTablePagination-actions': { ml: 0.5 },
          }}
        />
      </Box>
      <Box sx={{ mt: 1 }}>
        <EscritorioSelect
          value={escritorioId}
          onChange={(v) => setEscritorioId(v as number | null)}
          label="Escritório de destino"
          required
          size="small"
        />
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Importando: cadastro → contratos → serviços → faturas → PIX.
      </Typography>
      {importing && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={(importResults.length / selectedIds.size) * 100}
          />
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
            Processando {importResults.length} de {selectedIds.size}...
          </Typography>
        </Box>
      )}
      {importResults.length > 0 && (
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {importResults.map((r, i) => (
            <Card key={i} sx={{ mb: 0.5 }}>
              <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="caption" fontWeight="bold">
                    {r.nome || `ID ${r.id_ixc}`}
                  </Typography>
                  {r.status === 'sucesso' ? (
                    <Chip label="Sucesso" color="success" size="small" sx={{ height: 10, fontSize: '0.65rem' }} />
                  ) : (
                    <Chip label="Erro" color="error" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                </Box>
                {r.erro && (
                  <Typography variant="caption" color="error" display="block" fontSize="0.7rem">
                    {r.erro}
                  </Typography>
                )}
                {r.detalhes && r.status === 'sucesso' && (
                  <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">
                    ID: {r.detalhes.cliente_id} · Ctr: {r.detalhes.contratos_importados ?? 0} · Srv: {r.detalhes.servicos_importados ?? 0} · Fat: {r.detalhes.faturas_importadas ?? 0} · PIX: {r.detalhes.pix_atualizados ?? 0}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight="bold">Importar do IXC</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 1, pt: 0 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 1.5, '& .MuiStepLabel-root': { py: 0 } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {getStepContent()}
        <style>
          {`
            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            .animate-spin { animation: spin 1s linear infinite; }
          `}
        </style>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} size="small">Cancelar</Button>
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleBuscarClientes} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar clientes'}
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} size="small">Voltar</Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleIniciarImportacao}
              disabled={selectedIds.size === 0 || !escritorioId}
            >
              Importar {selectedIds.size} selecionado(s)
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)} disabled={importing} size="small">Voltar</Button>
            {importResults.length === 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleExecutarImportacao}
                disabled={importing}
                startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
              >
                {importing ? 'Importando...' : 'Importar selecionados'}
              </Button>
            )}
            {importResults.length > 0 && (
              <Button variant="contained" onClick={handleClose} size="small">
                Fechar
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportIxcModal;
